import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin, AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import CICOValidationLabel from 'inventory/check-in-check-out/CICOValidationLabel';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalChipListField from 'ui-library/chips/HorizontalChipListField';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

interface Props {
  items: Array<SampleStatusItem>;
  onRequestClose: () => void;
  onCheckIn: (itemIds: Array<string>) => void;
  analyticsMetadata?: AnalyticsMetadataForContext;
  shouldAlwaysShowSampleStatusTable?: boolean;
}

export default function CheckInModal(props: Props): JSX.Element {
  const itemIds = [...new Set(props.items.map((item) => item.id))];

  const [canProceedOnSampleStatus, setCanProceedOnSampleStatus] = useState(false);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Array<string>>([]);

  const [shouldShowStatusWarnings, setShouldShowStatusWarnings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [samples, setSamples] = useState<Array<SampleStatusItem> | null>(null);
  const [eventGroupId, setEventGroupId] = useState('');

  useOnMount(() => {
    async function checkForWarnings(): Promise<void> {
      const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
        sampleIds: itemIds,
        shouldAlwaysShowWarning: props.shouldAlwaysShowSampleStatusTable,
      });
      setShouldShowStatusWarnings(shouldShowWarning);
      setSamples(samples ?? null);
      setSelectedSampleIds(itemIds);
      setLoading(false);
    }
    checkForWarnings();
    setEventGroupId(Analytics.makeAnalyticsEventId());
  });

  const statusWarningTrackingParams: BaseSampleStatusWarningTrackingParams = {
    actionOrigin: props.analyticsMetadata?.actionOrigin ?? ActionOrigin.UI_UNDETERMINED,
    actionAttempted: 'check_in',
    eventGroupId: props.analyticsMetadata?.eventGroupId ?? eventGroupId,
  };

  const possibleWarning =
    shouldShowStatusWarnings && samples != null ? (
      <SampleStatusWarning
        sampleIds={itemIds}
        selectedSampleIds={selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        shouldProceed={canProceedOnSampleStatus}
        setShouldProceed={setCanProceedOnSampleStatus}
        trackingParams={statusWarningTrackingParams}
        samples={samples}
      />
    ) : null;

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Check in',
          disabled: !canProceedWithSamples(
            selectedSampleIds,
            shouldShowStatusWarnings,
            canProceedOnSampleStatus
          ),
        },
      ]}
      footer={!shouldShowStatusWarnings && <CICOValidationLabel items={props.items} />}
      footerPullRight={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        Context.usingAsync(
          {
            analyticsMetadata: {
              ...props.analyticsMetadata,
              eventGroupId: props.analyticsMetadata?.eventGroupId ?? eventGroupId,
            },
          },
          null,
          () => {
            props.onCheckIn(selectedSampleIds);
          }
        );
        props.onRequestClose();
      }}
      title="Check in"
    >
      {loading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          {possibleWarning}
          {canProceedWithSamples(selectedSampleIds, shouldShowStatusWarnings, canProceedOnSampleStatus) && (
            <HorizontalChipListField ids={selectedSampleIds} title="Selected items" chipsAreLinks={false} />
          )}
        </>
      )}
    </Modal>
  );
}
